import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Menu, X, LogOut, Shield, ChevronDown } from "lucide-react";
import { useState } from "react";
import { PhoneLink } from "./PhoneCTA";
import { ScheduleConsultationCTA } from "./ScheduleConsultationCTA";

// Phase C — new information architecture. "Shop Peptide Therapies" is the
// commercial-intent entry into the treatment catalog (peptide library);
// Conditions / Providers / How It Works / Pricing / Resources give patients
// a clear map of the site's authoritative surfaces.
const links = [
  { to: "/peptide-library", label: "Treatments" },
  { to: "/conditions", label: "Conditions" },
  { to: "/providers", label: "Providers" },
  { to: "/locations", label: "Locations" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/pricing", label: "Pricing" },
];

// Resources dropdown entries — primary link + sub-links.
const RESOURCES_LINKS = [
  { to: "/research",                        label: "Research Library" },
  { to: "/articles/local-peptide-guides",   label: "Local Peptide Guides" },
];

/** Hover-activated Resources dropdown for the desktop nav. */
function ResourcesDropdown() {
  return (
    <div className="relative group" data-testid="nav-resources-dropdown">
      <button
        className="flex items-center gap-1 px-3 py-2 text-sm rounded-full font-medium text-stone-700 hover:text-[color:var(--vv-teal)] transition-colors duration-200"
        aria-haspopup="true"
        data-testid="nav-resources-trigger"
      >
        Resources <ChevronDown size={13} className="opacity-60 group-hover:rotate-180 transition-transform duration-200" />
      </button>
      {/* Dropdown panel — appears on hover via CSS group */}
      <div className="absolute top-full left-0 mt-1 w-52 rounded-xl border border-stone-200 bg-white shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
        <div className="py-2">
          {RESOURCES_LINKS.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `block px-4 py-2.5 text-sm font-medium transition-colors duration-150 ${
                  isActive
                    ? "text-[color:var(--vv-teal)] bg-amber-50"
                    : "text-stone-700 hover:text-[color:var(--vv-teal)] hover:bg-stone-50"
                }`
              }
              data-testid={`nav-resources-${to.replace(/\//g, "-").replace(/^-/, "")}`}
            >
              {label}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-stone-200">
      <div className="vv-container flex items-center justify-between h-20">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group flex-shrink-0 min-w-0">
          <img
            src="https://customer-assets-lqy194kg.emergentagent.net/job_therapy-finder-21/artifacts/uljvdqoh_gold-full-logo.webp"
            alt="MedicalPeptides.ai — powered by VidaVital Medical"
            className="h-12 md:h-14 w-auto object-contain flex-shrink-0"
          />
          <div className="hidden 2xl:block leading-tight border-l border-stone-200 pl-3">
            <div className="font-display font-bold text-[13px] tracking-tight text-stone-900 whitespace-nowrap">MedicalPeptides<span className="text-[color:var(--vv-gold-deep)]">.ai</span></div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 mt-0.5 whitespace-nowrap">Powered by VidaVital Medical</div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.exact}
              data-testid={`nav-${l.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm rounded-full font-medium transition-colors duration-200 ${
                  isActive ? "text-[color:var(--vv-teal)] bg-amber-50" : "text-stone-700 hover:text-[color:var(--vv-teal)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <ResourcesDropdown />
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <PhoneLink location="header_desktop" testId="header-phone" className="text-sm" />
          {user ? (
            <>
              <Link to="/admin" data-testid="header-admin-dashboard" className="vv-btn-outline text-sm">
                <Shield size={16} /> Admin
              </Link>
              <button data-testid="header-logout" onClick={() => { logout(); navigate("/"); }} className="vv-btn-outline text-sm">
                <LogOut size={16} /> Logout
              </button>
            </>
          ) : null}
          <Link
            to="/peptide-library"
            data-testid="header-shop-peptide-therapies"
            className="vv-btn-outline text-sm"
          >
            Shop Peptide Therapies
          </Link>
          <ScheduleConsultationCTA
            location="header-desktop"
            variant="gold"
            size="md"
          />
        </div>

        <button data-testid="header-menu-toggle" className="xl:hidden p-2" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
          <div className="xl:hidden border-t border-stone-200 bg-white">
          <div className="vv-container py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.exact}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg font-medium ${isActive ? "text-[color:var(--vv-teal)] bg-amber-50" : "text-stone-700"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {/* Resources sub-links expanded inline for mobile */}
            {RESOURCES_LINKS.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg font-medium pl-6 ${isActive ? "text-[color:var(--vv-teal)] bg-amber-50" : "text-stone-600"}`
                }
                data-testid={`mobile-nav-${to.replace(/\//g, "-").replace(/^-/, "")}`}
              >
                {label}
              </NavLink>
            ))}
            <Link
              to="/peptide-library"
              onClick={() => setOpen(false)}
              className="vv-btn-outline text-center mt-2"
            >
              Shop Peptide Therapies
            </Link>
            <ScheduleConsultationCTA
              location="header-mobile"
              variant="gold"
              size="lg"
              fullWidth
            >
              Start Consultation
            </ScheduleConsultationCTA>
            <div className="mt-3 flex justify-center">
              <PhoneLink location="header_mobile_menu" testId="mobile-phone" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
